import { GOOGLE_SHEETS_CONFIG, CATEGORIES } from "../config";
import * as authService from "./authService";
import { updateUrlWithSheetId as updateUrlWithSpreadsheetId } from "../utils/shared/urlOperations";

/**
 * Google Sheets Service
 *
 * Provides methods for interacting with Google Sheets API v4
 * Uses API key authentication for read-only operations
 * Uses OAuth2 authentication for write operations
 */

// Initialize OAuth with the configuration from config.js
if (GOOGLE_SHEETS_CONFIG.oauth) {
  authService.initOAuth(GOOGLE_SHEETS_CONFIG.oauth);
}

/**
 * Validates the Google Sheets configuration
 * @returns {boolean} True if configuration is valid
 * @throws {Error} If configuration is invalid
 */
const validateConfig = () => {
  const { spreadsheetId, sheetRange } = GOOGLE_SHEETS_CONFIG;

  if (!spreadsheetId) {
    throw new Error("Google Sheets spreadsheet ID is required");
  }

  if (!sheetRange) {
    throw new Error("Google Sheets range is required");
  }

  return true;
};

/**
 * Builds the Google Sheets API URL
 * @param {boolean} useOAuth - Whether to use OAuth (no API key in URL)
 * @returns {string} The API URL
 */
const buildApiUrl = () => {
  const { spreadsheetId, sheetRange } = GOOGLE_SHEETS_CONFIG;
  return `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(
    sheetRange
  )}`;
};

/**
 * Parses the response from Google Sheets API
 * @param {Object} response - The API response
 * @returns {Array} Parsed data with header keys
 */
const parseSheetData = (response) => {
  if (!response.values || !response.values.length) {
    return [];
  }

  const [headers, ...rows] = response.values;

  // Check if there's a Category column
  const categoryColumnIndex = headers.findIndex(
    (header) => header.toLowerCase() === "category"
  );

  return rows.map((row, index) => {
    const item = { id: index + 1 };

    // Map each cell to its corresponding header
    headers.forEach((header, i) => {
      if (i < row.length) {
        item[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
      } else {
        item[header.toLowerCase().replace(/\s+/g, "_")] = "";
      }
    });

    // Add a flag indicating if this row has a category
    if (categoryColumnIndex !== -1) {
      const categoryValue = row[categoryColumnIndex] || "";
      item.isCategorized = categoryValue.trim() !== "";
    } else {
      item.isCategorized = false;
    }

    return item;
  });
};

/**
 * Fetches data from Google Sheets
 * @returns {Promise<Array>} The sheet data
 * @throws {Error} If the API request fails
 */
export const fetchSheetData = async () => {
  try {
    // Validate configuration
    validateConfig();

    // Determine if we should use OAuth for this operation
    const useOAuth =
      GOOGLE_SHEETS_CONFIG.oauth &&
      GOOGLE_SHEETS_CONFIG.oauth.useForAllOperations;

    // Build API URL
    const apiUrl = buildApiUrl(useOAuth);

    // Fetch data from Google Sheets API
    let response;

    if (useOAuth) {
      // Use authenticated fetch if OAuth is enabled for all operations
      response = await authService.authenticatedFetch(apiUrl);
    } else {
      // Otherwise use regular fetch with API key
      response = await fetch(apiUrl);
    }

    // Check if the request was successful
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Google Sheets API error: ${response.status} ${response.statusText}${
          errorData.error ? ` - ${errorData.error.message}` : ""
        }`
      );
    }

    // Parse the response
    const data = await response.json();

    // Check if "Category" column exists, add it if it doesn't
    await ensureCategoryColumnExists(data);

    // Update URL with the current spreadsheet ID and sheet range
    updateUrlWithSpreadsheetId(
      GOOGLE_SHEETS_CONFIG.spreadsheetId,
      GOOGLE_SHEETS_CONFIG.sheetRange
    );

    // Parse and return the sheet data
    return parseSheetData(data);
  } catch (error) {
    console.error("Error fetching sheet data:", error);
    throw error;
  }
};

/**
 * Fetches sheet metadata (titles, properties)
 * @returns {Promise<Object>} The sheet metadata
 * @throws {Error} If the API request fails
 */
export const fetchSheetMetadata = async () => {
  try {
    // Validate configuration
    validateConfig();

    const { spreadsheetId } = GOOGLE_SHEETS_CONFIG;

    // Determine if we should use OAuth for this operation
    const useOAuth =
      GOOGLE_SHEETS_CONFIG.oauth &&
      GOOGLE_SHEETS_CONFIG.oauth.useForAllOperations;

    // Build the metadata URL
    const metadataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}`;

    // Fetch metadata from Google Sheets API
    let response;

    if (useOAuth) {
      // Use authenticated fetch if OAuth is enabled for all operations
      response = await authService.authenticatedFetch(metadataUrl);
    } else {
      // Otherwise use regular fetch with API key
      response = await fetch(metadataUrl);
    }

    // Check if the request was successful
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Google Sheets API error: ${response.status} ${response.statusText}${
          errorData.error ? ` - ${errorData.error.message}` : ""
        }`
      );
    }

    // Parse and return the metadata
    return await response.json();
  } catch (error) {
    console.error("Error fetching sheet metadata:", error);
    throw error;
  }
};

/**
 * Gets available sheet names and IDs
 * @returns {Promise<Array>} Array of sheet objects with title and sheetId
 */
export const getAvailableSheets = async () => {
  try {
    const metadata = await fetchSheetMetadata();

    if (!metadata.sheets) {
      return [];
    }

    return metadata.sheets.map((sheet) => ({
      title: sheet.properties.title,
      sheetId: sheet.properties.sheetId,
      index: sheet.properties.index,
    }));
  } catch (error) {
    console.error("Error getting available sheets:", error);
    throw error;
  }
};

/**
 * Updates a cell in Google Sheets with category data
 * @param {number} rowIndex - The index of the row to update (0-based)
 * @param {string} categoryId - The category ID to store
 * @returns {Promise<Object>} The update result
 * @throws {Error} If the API request fails
 */
export const updateSheetWithCategory = async (rowIndex, categoryId) => {
  try {
    // Validate configuration
    validateConfig();

    const { sheetRange } = GOOGLE_SHEETS_CONFIG;

    // Parse the range to get the sheet name
    const sheetName = sheetRange.split("!")[0].replace(/'/g, "");

    // Get the category name from the ID
    const category = CATEGORIES.find((cat) => cat.id === categoryId);
    const categoryName = category ? category.name : categoryId;

    // Calculate the actual row index in the sheet (add 2 for header row and 0-based index)
    const actualRowIndex = rowIndex + 2;

    // Determine the column letter for the category column
    // We'll add it as the next column after the last column in the range
    const metadata = await fetchSheetMetadata();
    const sheet = metadata.sheets.find((s) => s.properties.title === sheetName);

    if (!sheet) {
      throw new Error(`Sheet "${sheetName}" not found`);
    }

    // Get the current data to determine the last column
    const data = await fetchSheetData();
    if (!data || data.length === 0) {
      throw new Error("No data found in sheet");
    }

    // Find the row we want to update
    const rowToUpdate = data[rowIndex];
    if (!rowToUpdate) {
      throw new Error(`Row at index ${rowIndex} not found`);
    }

    // Get the headers from the first row of the original data
    // Use authenticated fetch with OAuth for this operation
    const response = await authService.authenticatedFetch(buildApiUrl());
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Google Sheets API error: ${response.status} ${response.statusText}${
          errorData.error ? ` - ${errorData.error.message}` : ""
        }`
      );
    }
    const jsonResponse = await response.json();
    const headers = jsonResponse.values[0];

    // Check if "Category" column already exists
    let categoryColumnIndex = headers.findIndex(
      (header) => header === "Category"
    );

    // If "Category" column doesn't exist, we'll need to add it
    // For now, we'll use the existing column if it exists, or the next available column
    const updateRange =
      categoryColumnIndex !== -1
        ? `'${sheetName}'!${String.fromCharCode(
            65 + categoryColumnIndex
          )}${actualRowIndex}`
        : `'${sheetName}'!${String.fromCharCode(
            65 + headers.length
          )}${actualRowIndex}`;

    // If we're adding a new column, we need to update the headers first
    if (categoryColumnIndex === -1) {
      const headerUpdateRange = `'${sheetName}'!${String.fromCharCode(
        65 + headers.length
      )}1`;

      // Update the header row with "Category"
      await updateSheetCell(headerUpdateRange, "Category");

      // Update the category column index for future reference
      categoryColumnIndex = headers.length;
    }

    // Update the cell with the category
    return await updateSheetCell(updateRange, categoryName);
  } catch (error) {
    console.error("Error updating sheet with category:", error);
    throw error;
  }
};

/**
 * Ensures that a "Category" column exists in the sheet
 * @param {Object} data - The data from the Google Sheets API
 * @returns {Promise<void>}
 */
const ensureCategoryColumnExists = async (data) => {
  try {
    if (!data.values || !data.values.length) {
      return;
    }

    const headers = data.values[0];

    // Check if "Category" column already exists
    const categoryColumnIndex = headers.findIndex(
      (header) => header.toLowerCase() === "category"
    );

    // If "Category" column doesn't exist, add it
    if (categoryColumnIndex === -1) {
      console.log("Category column not found, adding it...");

      // Parse the range to get the sheet name
      const { sheetRange } = GOOGLE_SHEETS_CONFIG;
      const sheetName = sheetRange.split("!")[0].replace(/'/g, "");

      // Determine the next available column letter
      const nextColumnLetter = String.fromCharCode(65 + headers.length);

      // Create the range for the header cell
      const headerRange = `'${sheetName}'!${nextColumnLetter}1`;

      // Add the "Category" header
      await updateSheetCell(headerRange, "Category");

      // Update the data object to include the new header
      data.values[0].push("Category");

      console.log("Category column added successfully");
    }
  } catch (error) {
    console.error("Error ensuring category column exists:", error);
    throw error;
  }
};

/**
 * Updates a specific cell in Google Sheets
 * @param {string} range - The range to update (e.g., 'Sheet1!A1')
 * @param {string} value - The value to set
 * @returns {Promise<Object>} The update result
 * @throws {Error} If the API request fails
 */
export const updateSheetCell = async (range, value) => {
  try {
    // Validate configuration
    validateConfig();

    // Check if OAuth is configured
    if (!GOOGLE_SHEETS_CONFIG.oauth) {
      throw new Error("OAuth configuration is required for write operations");
    }

    // Build the update URL (without API key since we'll use OAuth)
    const updateUrl = `https://sheets.googleapis.com/v4/spreadsheets/${
      GOOGLE_SHEETS_CONFIG.spreadsheetId
    }/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`;

    // Prepare the update data
    const updateData = {
      values: [[value]],
    };

    // Send the authenticated update request
    const response = await authService.authenticatedFetch(updateUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });

    // Check if the request was successful
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Google Sheets API error: ${response.status} ${response.statusText}${
          errorData.error ? ` - ${errorData.error.message}` : ""
        }`
      );
    }

    // Parse and return the response
    return await response.json();
  } catch (error) {
    console.error("Error updating sheet cell:", error);
    throw error;
  }
};

export default {
  fetchSheetData,
  fetchSheetMetadata,
  getAvailableSheets,
  updateSheetWithCategory,
  updateSheetCell,
};
